// @flow
import type { SourceType } from 'Common/player/types';
import type { State as AppState } from 'Common/types';
import { type ReceiverUiState } from 'Browser/cast/receiver/ReceiverUiStates';

export type CastButtonState = 'UNAVAILABLE' | 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED';

/* Both implementations of CastMethodsModule (`cast` func) consume JSONified object with the following structure. */
export type CastConfig = {|
  title: string,
  subtitle?: string,
  poster?: string,
  sourceType: SourceType,
  serializedStream: string,
  seekTo: number,
  slug?: string,
|};

/* Data stored in 'contentId' field of LoadRequest instance. */
export type CastContentConfig = {|
  serializedStream: string /* JSONified payload of SET_PLAYER_STREAM, SET_DVR_STREAM or SET_LIVE_STREAM action */,
  seekTo: number /* playback starts with that offset (in seconds) */,
  client: 'android' | 'web' /* Platform initiated the cast request. */,
  slug: string,
  nielsenDemographicId: string,
  nielsenOptOutStatus: boolean,
  deviceId: string,
  deviceVersion: string,
  deviceFriendlyName: string,
  deviceModelName: string,
|};

export type AdBreak = {|
  url: string,
  type: string,
  position: string,
|};

type AdSlot = {|
  end: string,
  slotType: string,
  start: string,
|};

export type Metadata = {|
  title?: string,
  subtitle?: string,
  posterUrl: string,
  images?: Array<{ url: string }> | void,
  freewheel?: { adSlots: Array<AdSlot> },
|};

/* Native and web module interface */
export type CastMethodsModuleInterface = {
  cast: (castConfig: string) => Promise<void> /* castConfig is a JSONified CastConfig instance */,
  sendCustomMessage: (payload: string) => Promise<void>,
};

export type SessionState =
  | 'CAST_SESSION_STARTED'
  | 'CAST_SESSION_STARTING'
  | 'CAST_SESSION_START_FAILED'
  | 'CAST_SESSION_RESUMED'
  | 'CAST_SESSION_RESUMING'
  | 'CAST_SESSION_RESUME_FAILED'
  | 'CAST_SESSION_SUSPENDED'
  | 'CAST_SESSION_DISCONNECTED'
  | 'CAST_SESSION_ENDING'
  | 'CAST_SESSION_ENDED';

/* Types of Chromecast SDK events that describe state of the media element. */
export type PlayerState =
  | 'IDLE'
  | 'PLAYING'
  | 'PAUSE'
  | 'PAUSED'
  | 'REQUEST_SEEK'
  | 'SEEKING'
  | 'SEEKED'
  | 'PLAYER_LOADING'
  | 'BUFFERING'
  | 'REQUEST_LOAD'
  | 'REQUEST_PAUSE'
  | 'REQUEST_PLAY'
  | 'PLAYER_LOAD_COMPLETE'
  | 'ERROR';

export type State = {|
  sessionState: SessionState,
  castButtonState: CastButtonState,
  deviceName?: string,
  error?: string,
  playingOnCastDevice: boolean,
  storeHasBeenSynced: boolean,
  sourceType: SourceType | void,
  currentVideoProgress?: number,
|};

export type Action =
  | {| isGoogleCastAction: true, type: SessionState, deviceName?: string, error?: string |}
  | {| isGoogleCastAction: true, type: 'CAST_BUTTON_STATE_CHANGED', buttonState: CastButtonState |}
  | {|
      isGoogleCastAction: true,
      type: 'CAST_PLAYING_ON_CAST_DEVICE',
      isPlaying: boolean,
      error?: string,
    |}
  | {| isGoogleCastAction: true, type: 'CAST_SET_STORE', payload: $Shape<AppState> |}
  | {| isGoogleCastAction: true, type: 'CAST_STORE_HAS_BEEN_SYNCED', payload: boolean |}
  | {| isGoogleCastAction: true, type: 'CAST_SET_SOURCE_TYPE', payload: SourceType |}
  | {| isGoogleCastAction: true, type: 'CAST_SET_VIDEO_PROGRESS', progress: number |};

type AustinProgramEpisode = { content_id: string, title: string, description: string };

export type AustinProgramInfo = {|
  callsign: string,
  channel_padded: string,
  content_id: string,
  content_type: string,
  duration: number,
  end_date_time: string,
  episode_description: string,
  episode_id: string,
  episode_theme: string,
  episode_title: string,
  event_name: string,
  is_hd: boolean,
  is_new: boolean,
  originalairdate: string,
  program_id: number,
  program_title: string,
  series_id: string,
  service_id: number,
  start_date_time: string,
  mediaview: Array<AustinProgramEpisode>,
|};

export type AustinProgramInfoRequest =
  | { result: 'ok', info: AustinProgramInfo }
  | { result: 'error', reason: string };

export type LLInfoPayload = {|
  timeShift: number,
  maxTimeShift: number,
  bufferedRanges: Array<{ end: number, start: number }>,
|};

type DvrInfoPayload = {| duration: number, currentTime: number |};

export type SubtitlesTrack = {|
  enabled: boolean,
  id: string,
  kind: string,
  label: string,
  lang: string,
  url: ?string,
|};

export type SubtitlesConf = {| enabled: true, trackId: string |} | {| enabled: false |};

/* Describes types of custom messages expected by the receiver app */
export type CastSenderToReceiverMessageType =
  | {| type: 'RESET_STORE', payload: $Shape<AppState> |} /* Merges payload and global store */
  | {| type: 'LL_TIME_SHIFT', payload: number |} /* Requests "timeShift" for LL content */
  | {| type: 'LL_PLAY' |} /* Request LL play (after stitching remotePlayer stops working) */
  | {| type: 'LL_PAUSE' |} /* Request LL pause (after stitching remotePlayer stops working) */
  | {| type: 'AUSTIN_CONFIGURE', payload: Object |} /* Tunes Austin to a new channel/dvrId */
  | {| type: 'AUSTIN_GET_DVR_INFO' |} /* Requests info about current DVR event  */
  | {| type: 'SHUT_DOWN' |} /* Requests receiver shut down */
  | {| type: 'CONFIGURE_SUBTITLES', payload: SubtitlesConf |} /* Enables or disables subtitles */
  | {| type: 'SEEK_TO', payload: number |} /* Requests seek to a certain position (web only) */
  | {|
      type: 'SEEK_BY',
      payload: number,
    |} /* Requests seek by a positive or negative number of seconds (web only) */
  | {| type: 'GET_SUBTITLES_INFO' |}; /* Requests 'SUBTITLES_INFO' message from receiver */

/** Types of messages Cast Receiver sends to the Sender. */
export type CastReceiverToSenderMessageType =
  | {| type: 'AUSTIN_DVR_INFO', payload: DvrInfoPayload |} /* Sends DVR info */
  | {| type: 'RECEIVER_IS_IDLE' |} /* Notifies sender about "IDLE" state transition */
  | {| type: 'LL_INFO', payload: LLInfoPayload |} /* Sends LL info */
  | {| type: 'CURRENT_SOURCE_TYPE', payload: { sourceType: SourceType } |}
  | {| type: 'SUBTITLES_INFO', payload: {| tracks: Array<SubtitlesTrack> |} |};

export type CastType =
  | 'SEND_LOAD_MEDIA_REQUEST' /* Triggers "real" LoadMedia request */
  | 'SEND_AUSTIN_CONFIGURE_REQUEST'; /* Triggers Austin lib tuning to a new channel/dvrId */

/* Describes exceptions types which may happen in ChromeCastSlingPlayer module. */
type AustinErrorType = 'fail_to_bootstrap' | 'fail_to_connect' | 'fail_to_play_stream' | 'unknown';

/* Internal Austin errors */
type AustinErrorReason =
  | 'connectFailed'
  | 'boxNotReady'
  | 'streamLost'
  | 'connectionLost'
  | 'playerError'
  | 'forceTransfer'
  | 'unknownError';

export class AustinError extends Error {
  type: AustinErrorType;

  constructor(type: AustinErrorType, message: string) {
    super(message);
    this.type = type;
  }
}

type AustinStatus =
  | 'connecting'
  | 'startingStreaming'
  | 'streaming'
  | 'stoppingStreaming'
  | 'disconnecting'
  | 'disconnected';

export type AustinEvent =
  | {| type: 'onStatusChange', payload: AustinStatus |}
  | {| type: 'onPopUp', payload: { type: 'dismiss' } |}
  | {|
      type: 'onStallStart',
      payload: { type: 'stallstarted' | 'seek' | 'seeked', timestamp: number, paused: boolean },
    |}
  | {|
      type: 'onStallEnd',
      payload: { type: 'stallended' | 'seeked', timestamp: number, paused: boolean },
    |}
  | {| type: 'onReady', payload: { timestamp: number, type: 'ready' } |}
  | {|
      type: 'onInfo',
      payload: { type: 'discontinuity', reason: 'channel_change' | 'buffer_flush', count: number },
    |}
  | {|
      type: 'onPause',
      payload: { time: number, issuer: 'api', timestamp: number, type: 'paused' },
    |}
  | {|
      type: 'onError',
      payload:
        | AustinError
        | {
            info: {|
              boxSessionId: number,
              clientSessionId: string,
              discontinuityCount: number,
              playbackSessionId: string,
              sessionDuration: number,
              code?: number,
              info?: string,
            |},
            reason?: AustinErrorReason | string,
            message?: string,
          },
    |}
  | { type: 'onTimeUpdate' }
  | { type: 'onPlaying' }
  | { type: 'configure' }; /* Synthetic event: triggers in response to "AUSTIN_CONFIGURE" action */

export type CommonUiTransition = 'idle' | 'requestLoad';

export type AustinUiTransition =
  | 'connect'
  | 'startStreaming'
  | 'stopStreaming'
  | 'stream'
  | 'austinSeek'
  | 'pauseAustin'
  | 'changeChannel'
  | 'disconnect'
  | 'austinError';

export type BitmovinUiTransition =
  | 'loadStream'
  | 'play'
  | 'pauseBitmovin'
  | 'playbackFinished'
  | 'bitmovinError';

export type ReceiverUiTransition = CommonUiTransition | AustinUiTransition | BitmovinUiTransition;

export type UiTransitionConfig<T> = {|
  name: T,
  from: ReceiverUiState | Array<ReceiverUiState>,
  to: ReceiverUiState,
|};

export type Cast = { framework: Object, player: Object };
export type Chrome = { cast: Object };
